import React, { useEffect, useRef, useState } from "react";
import {
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Modal,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import axios from "../api/axios";
import PrimaryButton from "../components/PrimaryButton";
import { loadScript } from "../utils/razorpay";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import DeleteIcon from "@mui/icons-material/Delete";
import LogoutIcon from "@mui/icons-material/Logout";
import { useSocket } from "../context/SocketProvider";

const OrderPayment = () => {
    const outlet = localStorage.getItem("outlet");
    const socket = useSocket();
    const navigate = useNavigate();

    const kioskUser = JSON.parse(localStorage.getItem("kioskUser"));
    const pineLabCredentials = JSON.parse(localStorage.getItem("pineLabCredentials"));

    const ordersRef = useRef(null);           
    const pollingIntervalRef = useRef(null);    
    const txnIdRef = useRef("");

    const [cartItems, setCartItems] = useState([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [additionalCharges, setAdditionalCharges] = useState({});
    const [userDetails, setUserDetails] = useState(null);
    const [makingTime, setMakingTime] = useState("");
    const makingTimeRef = useRef(""); 
    const [makingTimeInMinutes, setMakingTimeInMinutes] = useState("");
    const makingTimeInMinutesRef = useRef("");
    const [isLoading, setIsLoading] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    const [qtyChangingIds, setQtyChangingIds] = useState([]);
    const [deletingIds, setDeletingIds] = useState([]);
    const [fetchTime, setFetchTime] = useState(false);
    const [orders, setOrders] = useState(null);
    const [isQRPayment, setIsQRPayment] = useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(false);
    const [txnId, setTxnId] = useState("");
    const [paymentStatus, setPaymentStatus] = useState("");
    const [selectedMode, setSelectedMode] = useState(null);
    const [paymentEnabled, setPaymentEnabled] = useState(false);
    const [paymentType, setPaymentType] = useState("");
    const [payButtonLoading, setPayButtonLoading] = useState(false);
    const [prices, setPrices] = useState({
        totalPrice: 0,
        quantity: 0,
        totalTaxable: 0,
        totalTaxAmount: 0,
        grandTotal: 0,
        savedAmount: 0,
    });

    // Keep txnIdRef in sync with txnId state
    useEffect(() => {
        txnIdRef.current = txnId;
    }, [txnId]);

    // Keep makingTime refs in sync
    useEffect(() => {
        makingTimeRef.current = makingTime;
        makingTimeInMinutesRef.current = makingTimeInMinutes;
    }, [makingTime, makingTimeInMinutes]);

    const paymentModes = [
        { id: 1, code: 1, name: "Card", src: "/assets/card.png" },
        { id: 2, code: 10, name: "UPI", src: "/assets/upi.png" },
    ];

    const handleLogout = async () => {
        const currentCartId = localStorage.getItem("cartId");
        const hasItems = cartItems.length > 0;

        if (currentCartId && hasItems) {
            await deleteCart(currentCartId);
        }

        clearCartLocally();
        localStorage.removeItem("kioskUser");
        localStorage.removeItem("orderAmount");
        localStorage.removeItem("orderId");
        localStorage.removeItem("rzp_checkout_anon_id");
        localStorage.removeItem("rzp_stored_checkout_id");
        localStorage.removeItem("rzp_device_id");

        navigate("/explore");
    };

    const clearCartLocally = () => {
        localStorage.removeItem("cart");
        localStorage.removeItem("cartItems");
        localStorage.removeItem("cartId");
        localStorage.removeItem("additionalCharges");
        localStorage.removeItem("cartReadyTime");
    };

    const getCartItems = async () => {
        setPayButtonLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`cart/userCart/new/${outlet}/PICKUP`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const grandTotal = response?.data?.data?.cart?.grandTotal;
                setAdditionalCharges(response?.data?.data?.additionalCharges);
                setTotalAmount(grandTotal);

                const items = response?.data?.data?.cartItems || [];
                setCartItems(items);

                localStorage.setItem("cartItems", JSON.stringify(items));
                localStorage.setItem(
                    "additionalCharges",
                    JSON.stringify(response?.data?.data?.additionalCharges)
                );

                if (items.length === 0) {
                    localStorage.removeItem("cartId");
                }

                await getCartReadyTime();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setPayButtonLoading(false);
        }
    };

    // FIX 2: consistent endpoint — using "cart" (same as Products.jsx)
    const deleteCartItem = async (id) => {
        setDeletingIds((prev) => [...prev, id]);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.delete("cart/new", {
                headers: { Authorization: `Bearer ${token}` },
                data: { cartItemIds: [id] },
            });

            if (response?.status === 200) {
                const message = response?.data?.data?.message;
                toast.success(message, {
                    position: "bottom-center",
                    autoClose: 2000,
                    theme: "colored",
                });

                setCartItems((prevItems) => {
                    const updated = prevItems.filter((item) => item._id !== id);
                    // FIX 10: clear cartId when last item removed
                    if (updated.length === 0) {
                        localStorage.removeItem("cartId");
                    }
                    return updated;
                });

                await getCartReadyTime();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setDeletingIds((prev) => prev.filter((itemId) => itemId !== id));
        }
    };

    const deleteCart = async (id) => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.delete("cart/new", {
                headers: { Authorization: `Bearer ${token}` },
                data: { cartId: id },
            });
        } catch (error) {
            console.error("deleteCart error:", error?.response?.data || error.message);
        }
    };

    const handleQuantityChange = async (productId, newQuantity) => {
        if (qtyChangingIds.includes(productId)) return;

        setQtyChangingIds((prev) => [...prev, productId]);
        const previousCartItems = JSON.parse(JSON.stringify(cartItems));

        try {
            if (newQuantity <= 0) {
                setCartItems((prev) => {
                    const updated = prev.filter((item) => item._id !== productId);
                    if (updated.length === 0) localStorage.removeItem("cartId");
                    return updated;
                });
                await deleteCartItem(productId);
                return;
            }

            setCartItems((prev) =>
                prev.map((item) =>
                    item._id === productId ? { ...item, quantity: newQuantity } : item
                )
            );

            const token = localStorage.getItem("authToken");
            const response = await axios.put(
                "cart/new",
                { cartItems: [{ _id: productId, quantity: newQuantity }] },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response?.status === 200) {
                getCartItems();
            }
        } catch (error) {
            setCartItems(previousCartItems);
            handleApiError(error);
        } finally {
            setQtyChangingIds((prev) => prev.filter((id) => id !== productId));
        }
    };

    const getCartReadyTime = async () => {
        setFetchTime(true);
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get("cart/getMakingTIme", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const readyTime = response?.data?.data?.readyTime;
                const time = response?.data?.data?.makingTimeDuration.split(" ")[0];

                localStorage.setItem("cartReadyTime", readyTime);
                setMakingTime(readyTime);
                setMakingTimeInMinutes(time);

                makingTimeRef.current = readyTime;
                makingTimeInMinutesRef.current = time;
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setFetchTime(false);
        }
    };

    const createOrder = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            if (!token) {
                return toast.error("Token is missing.", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            const payload = {
                ordersType: "PICKUP",
                paymentMode: "ONLINE",
                outlet,
                readyTime: makingTimeRef.current,
            };

            const response = await axios.post("order/createOrderNew", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.status === 201) {
                const orderId = response?.data?.data?.order?.id;
                localStorage.setItem("orderId", orderId);
                localStorage.setItem("orderAmount", response?.data?.data?.order?.amount);
                await proceedToPay(orderId);
                await handlePayment();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePayment = async () => {
        try {
            setBtnLoading(true);
            const order_id = localStorage.getItem("orderId");
            const amount = localStorage.getItem("orderAmount");
            const userPhone = kioskUser?.phone || "";

            if (!order_id || !amount) {
                return toast.error("Order ID or amount is missing", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
            }

            localStorage.setItem("isQRPayment", "false");

            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY,
                amount,
                currency: "INR",
                order_id,
                handler: function (response) {
                    if (!ordersRef.current) {
                        handlePaymentVerification(response);
                    }
                },
                prefill: {
                    name: "Xpanse",
                    contact: userPhone,
                },
                modal: {
                    ondismiss: function () {
                        localStorage.setItem("isQRPayment", "true");
                    },
                },
                method: {
                    upi: true,
                    netbanking: true,
                    card: true,
                    wallet: true,
                    paylater: false,
                    qr: true,
                },
                upi: { flow: "collect" },
            };

            const rzpInstance = new window.Razorpay(options);
            rzpInstance.open();
            window.razorpayInstance = rzpInstance;
        } catch (error) {
            console.error("Payment initiation failed:", error);
            toast.error("Payment initiation failed. Please try again.", {
                position: "bottom-center",
                autoClose: 3000,
                theme: "colored",
            });
        } finally {
            setBtnLoading(false);
        }
    };

    const proceedToPay = async (razorpayOrderId) => {
        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                ordersType: "PICKUP",
                paymentMode: "ONLINE",
                outlet,
                readyTime: makingTimeRef.current,
                razorpayOrderId,
                kioskUser,
                arrivalTimeDurationInMinutes: makingTimeInMinutesRef.current,
            };
            await axios.post("order/proceed-to-pay", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePaymentVerification = async (razorpay) => {
        setIsLoading(true);
        try {
            const authToken = localStorage.getItem("authToken");
            const latestKioskUser = JSON.parse(localStorage.getItem("kioskUser"));

            if (!authToken) throw new Error("Authentication token is missing.");

            const payload = {
                razorpayOrderId: razorpay?.razorpay_order_id,
                razorpayPaymentId: razorpay?.razorpay_payment_id,
                razorpaySignature: razorpay?.razorpay_signature,
                paymentMode: "ONLINE",
                outlet,
                readyTime: makingTimeRef.current,
                arrivalTimeDurationInMinutes: makingTimeInMinutesRef.current,
                kioskUser: latestKioskUser?._id,
            };

            const response = await axios.post("order/verifyPaymentNew", payload, {
                headers: { Authorization: `Bearer ${authToken}` },
            });

            if (response?.status === 200 || response?.status === 201) {
                const orderNum = response?.data?.data?.order?.order_no;
                const orderSequence = response?.data?.data?.order?.order_sequence;

                if (orderNum && orderSequence) {
                    localStorage.setItem("orderNumber", `#${orderSequence}${orderNum}`);
                } else {
                    // FIX 7: toast instead of throw inside async callback
                    toast.error("Order number is missing in the response.", {
                        position: "bottom-center",
                        autoClose: 3000,
                        theme: "colored",
                    });
                    return;
                }
                navigate("/order-confirmation");
            }
        } catch (error) {
            const message =
                error?.response?.data?.status === false
                    ? error.response.data.error
                    : "An unexpected error occurred.";
            toast.error(message, {
                position: "bottom-center",
                autoClose: 2000,
                theme: "colored",
            });
        } finally {
            setIsLoading(false);
        }
    };

    // ---------------------------------------------------------------------------
    // Pine Labs payment
    // ---------------------------------------------------------------------------

    const handlePaymentModalClosed = () => {
        setIsPaymentModalOpen(false);
        setTxnId("");
    };

    const generateRandom8DigitNumber = () => {
        return Math.floor(10000000 + Math.random() * 90000000);
    };

    const handlePinePayment = async () => {
        try {
            setIsPaymentModalOpen(true);
            const token = localStorage.getItem("authToken");
            const amount =
                cartItems
                    .filter((item) => item.quantity > 0)
                    .reduce((acc, product) => acc + (product.grandTotal || 0), 0) +
                (additionalCharges?.platformFees || 0) +
                (additionalCharges?.GSTPlatformFees || 0);

            const response = await axios.post(
                "pinelab/initiate-payment",
                {
                    TransactionNumber: generateRandom8DigitNumber(),
                    SequenceNumber: 1,
                    AllowedPaymentMode: selectedMode,
                    Amount: amount * 100,
                    UserID: kioskUser?.name,
                    MerchantID: pineLabCredentials.merchantId,
                    SecurityToken: pineLabCredentials.securityToken,
                    ClientID: pineLabCredentials.clientId,
                    StoreID: pineLabCredentials.storeId,
                    AutoCancelDurationInMinutes: 3,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const txnReferenceId =
                response?.data?.data?.response?.data?.response?.PlutusTransactionReferenceID;

            if (!txnReferenceId) throw new Error("Transaction ID is missing.");

            setTxnId(txnReferenceId);
            txnIdRef.current = txnReferenceId; // sync ref immediately
            setPaymentStatus("Pending");
            await initiatePinelabOrder(txnReferenceId);
        } catch (error) {
            toast.error("Payment initiation failed. Try again.", {
                position: "bottom-center",
                autoClose: 3000,
                theme: "colored",
            });
            setIsPaymentModalOpen(false);
        }
    };

    const initiatePinelabOrder = async (PlutusTransactionReferenceID) => {
        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                PlutusTransactionReferenceID,
                outlet,
                phoneNumber: kioskUser.phone,
            };
            const response = await axios.post("order/initiateKioskOrder", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.status === 201) {
                let shouldContinuePolling = true;

                const intervalId = setInterval(() => {
                    if (shouldContinuePolling) {
                        handleGetPinePaymentStatus(
                            PlutusTransactionReferenceID,
                            intervalId,
                            () => { shouldContinuePolling = false; }
                        );
                    } else {
                        clearInterval(intervalId);
                    }
                }, 3000);

                // FIX 5: store in ref so cleanup always works on unmount
                pollingIntervalRef.current = intervalId;
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleGetPinePaymentStatus = async (txnReferenceId, intervalId, stopPolling) => {
        try {
            setIsCheckingStatus(true);
            const token = localStorage.getItem("authToken");

            const response = await axios.post(
                "pinelab/check-payment-status",
                {
                    MerchantID: pineLabCredentials.merchantId,
                    SecurityToken: pineLabCredentials.securityToken,
                    ClientID: pineLabCredentials.clientId,
                    StoreID: pineLabCredentials.storeId,
                    PlutusTransactionReferenceID: txnReferenceId,
                    ordersType: "PICKUP",
                    paymentMode: "ONLINE",
                    outlet,
                    readyTime: makingTimeRef.current,
                    arrivalTimeDurationInMinutes: "12",
                    pickupOption: "DINE-IN",
                    phoneNumber: kioskUser?.phone,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const status =
                response?.data?.data?.pinelabData?.data?.response?.ResponseCode;
            setPaymentStatus(status ?? "Unknown");

            if (status === 0) {
                const { ResponseCode, ResponseMessage, TransactionData } =
                    response.data.data.pinelabData.data.response;

                await createPinelabOrder(
                    ResponseCode,
                    ResponseMessage,
                    TransactionData,
                    txnReferenceId
                );

                clearInterval(intervalId);
                pollingIntervalRef.current = null;
                stopPolling();

                toast.success("Payment successful!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });

                handlePaymentModalClosed();
                proceedWithOrder();
            } else if (status === 1) {
                clearInterval(intervalId);
                pollingIntervalRef.current = null;
                stopPolling();

                toast.error("Payment failed!", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });

                handlePaymentModalClosed();
            }
        } catch (error) {
            console.error("Error checking payment status:", error);
        } finally {
            setIsCheckingStatus(false);
        }
    };

    const createPinelabOrder = async (
        responseCode,
        responseMessage,
        transactionData,
        PlutusTransactionReferenceID
    ) => {
        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                outlet,
                readyTime: makingTimeRef.current,
                ordersType: "PICKUP",
                arrivalTimeDurationInMinutes: "12",
                paymentMode: "ONLINE",
                pickupOption: "DINE-IN",
                phoneNumber: kioskUser?.phone,
                PlutusTransactionReferenceID,
                responseCode,
                responseMessage,
                transactionData,
            };

            const response = await axios.post("order/createKioskOrder", payload, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const orderData = response.data?.data?.order;
                const orderNumber = `#${orderData.order_sequence}${orderData.order_no}`;
                localStorage.setItem("orderNumber", orderNumber);

                const message = response?.data?.data?.message;
                toast.success(message, {
                    position: "top-center",
                    autoClose: 2000,
                    theme: "colored",
                });

                setTxnId("");
                navigate("/order-confirmation");
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    // FIX 6: use pineLabCredentials from state instead of hardcoded strings
    const handlePinePaymentCancel = async () => {
        try {
            // FIX 5: clear polling interval ref
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }

            const token = localStorage.getItem("authToken");
            const amount = totalAmount * 100;

            const response = await axios.post(
                "pinelab/cancel-payment",
                {
                    MerchantID: pineLabCredentials.merchantId,       
                    SecurityToken: pineLabCredentials.securityToken,  
                    ClientID: pineLabCredentials.clientId,           
                    StoreID: pineLabCredentials.storeId,              
                    PlutusTransactionReferenceID: txnIdRef.current,
                    Amount: amount,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const responseCode =
                response?.data?.data?.response?.data?.response?.ResponseCode;
            const responseMessage =
                response?.data?.data?.response?.data?.response?.ResponseMessage;

            if (responseCode === 0) {
                toast.success("Payment cancelled successfully.", {
                    position: "bottom-center",
                    autoClose: 3000,
                    theme: "colored",
                });
                setPaymentStatus("Cancelled");
            } else {
                toast.error(responseMessage || "Unable to cancel payment.", {
                    position: "bottom-center",
                    autoClose: 4000,
                    theme: "colored",
                });
                setIsPaymentModalOpen(true);
                return;
            }

            setIsPaymentModalOpen(false);
        } catch (error) {
            console.error("Payment cancellation failed:", error);
            toast.error("An error occurred while canceling the payment.", {
                position: "bottom-center",
                autoClose: 3000,
                theme: "colored",
            });
            setIsPaymentModalOpen(true);
        }
    };

    const proceedWithOrder = () => {
        handlePaymentModalClosed();
        setIsCheckingStatus(false);
        setPaymentStatus("Approved");
    };

    const handleModeSelect = (code) => {
        setSelectedMode(code);
        setPaymentEnabled(true);
    };

    const getSingleOutletDetails = async (outletId) => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(`outlet?_id=${outletId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response?.data?.status === true) {
                const type =
                    response?.data?.data?.outlets.paymentType || "PAYMENT_GATEWAY";
                setPaymentType(type);
            }
        } catch (error) {
            if (error.response?.data?.status === false) {
                toast.error(error.response.data.error, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const calculateAllPrices = () => {
        const totalMRP = (
            cartItems?.reduce((acc, item) => {
                if (item.quantity > 0) {
                    const itemTotal = item.mrpRate * item.quantity;
                    const addonTotal =
                        (item.addons?.reduce((a, addon) => a + addon.price, 0) || 0) *
                        item.quantity;
                    return acc + itemTotal + addonTotal;
                }
                return acc;
            }, 0) || 0
        ).toFixed(2);

        const quantity = cartItems
            ?.filter((item) => item.quantity > 0)
            .reduce((acc, item) => acc + item.quantity, 0);

        const totalTaxable = cartItems
            ?.filter((item) => item.quantity > 0)
            .reduce((acc, product) => acc + product.taxable, 0)
            .toFixed(2);

        const taxAmount = cartItems
            ?.filter((item) => item.quantity > 0)
            .reduce((acc, product) => acc + product.taxAmount, 0)
            .toFixed(2);

        const grandTotal = (
            cartItems
                ?.filter((item) => item.quantity > 0)
                .reduce((acc, product) => acc + (product.grandTotal || 0), 0) +
            (additionalCharges?.platformFees || 0) +
            (additionalCharges?.GSTPlatformFees || 0)
        ).toFixed(2);

        const savedAmount = (totalMRP - totalTaxable).toFixed(2);

        setPrices({
            totalPrice: totalMRP,
            quantity,
            totalTaxable,
            totalTaxAmount: taxAmount,
            grandTotal,
            savedAmount,
        });
    };

    // ---------------------------------------------------------------------------
    // Shared error handler
    // ---------------------------------------------------------------------------
    const handleApiError = (error) => {
        if (!error?.response) return;
        let errorMessage = "Something went wrong.";
        const status = error.response?.status;
        if (status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
        else if (status === 412) errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error || error?.response?.data?.data?.title;
        else if (status === 500) errorMessage = error?.response?.data?.error;
        toast.error(errorMessage, {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
        });
    };

    // ---------------------------------------------------------------------------
    // Effects
    // ---------------------------------------------------------------------------

    // Keep ordersRef in sync
    useEffect(() => {
        ordersRef.current = orders;
    }, [orders]);

    // Recalculate prices whenever cartItems changes
    useEffect(() => {
        calculateAllPrices();
    }, [cartItems, additionalCharges]);

    // Initial data load
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("kioskUser"));
        setUserDetails(user);
        getCartItems();
        getSingleOutletDetails(outlet);
    }, []);

    // Load Razorpay script once
    useEffect(() => {
        const loadRazorpayScript = async () => {
            try {
                await loadScript("https://checkout.razorpay.com/v1/checkout.js");
            } catch (error) {
                console.error("Failed to load Razorpay script:", error);
            }
        };
        loadRazorpayScript();
    }, []);

    // FIX 5: cleanup polling on unmount using ref
    useEffect(() => {
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, []);

    // FIX 9: merged both socket useEffects into one to avoid double joinOutletRoom emit
    useEffect(() => {
        if (!socket || !outlet) return;

        socket.emit("joinOutletRoom", { outletId: outlet });

        // FIX 4: cleaned up duplicate/contradictory setOrders calls
        socket.on("orderCreated", (newOrder) => {
            setOrders((prevOrders) => {
                if (prevOrders) return prevOrders; // already set, don't overwrite
                return newOrder;
            });

            localStorage.setItem("orderId", newOrder?.order?._id);
            localStorage.setItem("orderAmount", newOrder?.order?.amount);

            if (
                localStorage.getItem("isQRPayment") === "true" &&
                window.razorpayInstance
            ) {
                window.razorpayInstance.close();
                localStorage.removeItem("isQRPayment");
            }
        });

        socket.on("updatePaymentType", () => {
            window.location.reload();
        });

        return () => {
            socket.off("orderCreated");
            socket.off("updatePaymentType");
        };
    }, [socket, outlet]);

    // Navigate to confirmation after order is received via socket
    useEffect(() => {
        if (!orders) return;

        const timer = setTimeout(() => {
            const orderNum = orders?.order?.order_no;
            const orderSequence = orders?.order?.order_sequence;

            if (orderNum && orderSequence) {
                localStorage.setItem("orderNumber", `#${orderSequence}${orderNum}`);
                navigate("/order-confirmation");
            } else {
                // FIX 7: toast instead of unhandled throw inside setTimeout
                toast.error("Order number is missing. Please contact support.", {
                    position: "bottom-center",
                    autoClose: 4000,
                    theme: "colored",
                });
            }
        }, 15000);

        return () => clearTimeout(timer);
    }, [orders]);

    // FIX 8: auto-cancel timer uses txnIdRef so it always has the latest txnId
    useEffect(() => {
        if (!isPaymentModalOpen) return;

        const timer = setTimeout(() => {
            handlePinePaymentCancel();
        }, 2 * 60 * 1000);

        return () => clearTimeout(timer);
    }, [isPaymentModalOpen]);

    // ---------------------------------------------------------------------------
    // Render
    // ---------------------------------------------------------------------------

    // FIX 14: proper full-screen centered spinner
    if (isLoading) {
        return (
            <Box
                sx={{
                    width: "100vw",
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#f0f4f8",
                }}
            >
                <CircularProgress size={80} thickness={5} sx={{ color: "#1976d2" }} />
            </Box>
        );
    }

    return (
        <>
            <AppBar position="fixed" color="inherit" elevation={0}>
                <Toolbar sx={{ padding: "10px", background: "#dfeef5" }}>
                    <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                        <img
                            src="/assets/brand-logo.png"
                            alt="Brand Logo"
                            style={{ height: 50, marginRight: 5 }}
                        />
                    </Box>
                    <Box sx={{ flexGrow: 0 }}>
                        <Button
                            variant="contained"
                            onClick={handleLogout}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                padding: "8px 12px",
                                backgroundColor: "#fff",
                                color: "#000",
                                "&:hover": { backgroundColor: "#f0f0f0" },
                            }}
                        >
                            <LogoutIcon sx={{ mb: 0.5, color: "#000" }} />
                            Close
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            <Box
                sx={{
                    height: "calc(100vh - 100px)",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    background: "#dfeef5",
                    paddingTop: "90px",
                }}
            >
                {/* Left — Cart Items */}
                <Box
                    sx={{
                        width: "62%",
                        bgcolor: "white",
                        boxSizing: "border-box",
                        borderTopRightRadius: "50px",
                        overflow: "hidden",
                    }}
                    p={2}
                >
                    <Stack direction="row" alignItems="center" gap={1}>
                        <KeyboardBackspaceIcon
                            sx={{
                                color: "#0E4961",
                                fontSize: 35,
                                padding: "4px",
                                borderRadius: "50%",
                                cursor: "pointer",
                            }}
                            onClick={() => navigate("/products")}
                        />
                        <Typography variant="h6" fontSize={25} color="#0E4961">
                            Your Cart
                        </Typography>
                    </Stack>

                    {cartItems.length > 0 && (
                        <Box pl={1}>
                            <Typography
                                textAlign="left"
                                variant="h5"
                                fontWeight="semibold"
                                color="#0E4961"
                                fontSize={20}
                            >
                                {userDetails?.name || "User Name"}
                            </Typography>
                            <Typography textAlign="left" variant="body1" fontSize={14}>
                                {userDetails?.email}
                            </Typography>
                            <Typography textAlign="left" variant="body1" fontSize={16}>
                                {userDetails?.phone || "User Phone"}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ width: "100%", overflowY: "auto", height: "80%", marginRight: "2%" }}>
                        {cartItems.length > 0 ? (
                            <Box pl={1}>
                                <Stack direction="column" mt={2}>
                                    {cartItems.map((item, index) => {
                                        const isFirst = index === 0;
                                        const isLast = index === cartItems.length - 1;
                                        return (
                                            <Box
                                                key={item._id}
                                                sx={{
                                                    position: "relative",
                                                    borderTopLeftRadius: isFirst ? "20px" : "0",
                                                    borderBottom:
                                                        (!isFirst && !isLast) || cartItems.length === 2
                                                            ? "2px dashed #e0e0e0"
                                                            : "none",
                                                    borderTop:
                                                        !isFirst && !isLast ? "2px dashed #e0e0e0" : "none",
                                                    borderTopRightRadius: isFirst ? "20px" : "0",
                                                    bgcolor: "#dfeef5",
                                                    minHeight: "70px",
                                                    borderBottomLeftRadius: isLast ? "20px" : "0",
                                                    borderBottomRightRadius: isLast ? "20px" : "0",
                                                    paddingTop: isFirst ? "10px" : "8px",
                                                }}
                                                p={2}
                                            >
                                                {item?.product?.isDelete && (
                                                    <Box
                                                        sx={{
                                                            position: "absolute",
                                                            top: 0, left: 0, right: 0, bottom: 0,
                                                            bgcolor: "rgba(0,0,0,0.3)",
                                                            zIndex: 1,
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            borderRadius: "inherit",
                                                            color: "red",
                                                            fontWeight: "bold",
                                                            fontSize: "1.2rem",
                                                        }}
                                                    >
                                                        OUT OF STOCK
                                                    </Box>
                                                )}
                                                <Stack direction="row" justifyContent="space-between">
                                                    <Box sx={{ width: "45%" }}>
                                                        <Typography
                                                            fontWeight="bold"
                                                            textAlign="left"
                                                            color="#0E4961"
                                                        >
                                                            {item?.product?.name}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ width: "20%" }}>
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            justifyContent="space-around"
                                                        >
                                                            {item?.quantity === 1 ? (
                                                                <DeleteIcon
                                                                    onClick={() => {
                                                                        if (!deletingIds.includes(item._id)) {
                                                                            handleQuantityChange(item._id, item.quantity - 1);
                                                                        }
                                                                    }}
                                                                    className="cursor-p"
                                                                    style={{
                                                                        fontSize: "1rem",
                                                                        color: "#fff",
                                                                        backgroundColor: "#fe7678",
                                                                        borderRadius: "50%",
                                                                        padding: "3px",
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Remove
                                                                    onClick={() => {
                                                                        if (!qtyChangingIds.includes(item._id)) {
                                                                            handleQuantityChange(item._id, item.quantity - 1);
                                                                        }
                                                                    }}
                                                                    className="cursor-p"
                                                                    style={{
                                                                        fontSize: "1rem",
                                                                        color: "#fff",
                                                                        backgroundColor: "#00b0ff",
                                                                        borderRadius: "50%",
                                                                        padding: "3px",
                                                                    }}
                                                                />
                                                            )}
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                justifyContent="center"
                                                                sx={{
                                                                    width: "20px",
                                                                    textAlign: "center",
                                                                    fontWeight: "bold",
                                                                    marginTop: "-1px",
                                                                }}
                                                            >
                                                                {qtyChangingIds.includes(item._id) ? (
                                                                    <CircularProgress size={16} color="success" />
                                                                ) : deletingIds.includes(item._id) ? (
                                                                    <CircularProgress size={16} color="error" />
                                                                ) : (
                                                                    item?.quantity
                                                                )}
                                                            </Stack>
                                                            <Add
                                                                onClick={() => {
                                                                    if (!qtyChangingIds.includes(item._id)) {
                                                                        handleQuantityChange(item._id, item.quantity + 1);
                                                                    }
                                                                }}
                                                                className="cursor-p"
                                                                style={{
                                                                    fontSize: "1rem",
                                                                    color: "#fff",
                                                                    backgroundColor: "#00b0ff",
                                                                    borderRadius: "50%",
                                                                    padding: "3px",
                                                                }}
                                                            />
                                                        </Stack>
                                                    </Box>
                                                    <Box sx={{ width: "30%" }}>
                                                        <Typography
                                                            fontWeight="bold"
                                                            textAlign="right"
                                                            sx={{ color: "#00b0ff" }}
                                                        >
                                                            ₹
                                                            {(item?.product.newOfferPrice || 0) *
                                                                (item?.quantity || 1) +
                                                                item?.addons?.reduce(
                                                                    (acc, a) => acc + (a?.price || 0),
                                                                    0
                                                                ) *
                                                                (item?.quantity || 1)}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                                <Grid container>
                                                    <Typography
                                                        sx={{ color: "#999", fontSize: "0.9rem", marginTop: "5px" }}
                                                    >
                                                        {item?.addons
                                                            ?.map((addon) => addon?.selectedValue)
                                                            ?.join(", ")}
                                                    </Typography>
                                                </Grid>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    flexDirection: "column",
                                    width: "90%",
                                    bgcolor: "white",
                                    marginTop: "50px",
                                }}
                            >
                                <img
                                    src="/assets/empty-cart.png"
                                    width="60%"
                                    style={{ marginBottom: "16px" }}
                                    alt="empty cart"
                                />
                                <Typography variant="h6" sx={{ color: "#555" }}>
                                    Your cart is currently empty
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Right — Order Summary + Payment */}
                <Box sx={{ flex: "0 0 33%", background: "#dfeef5", marginRight: "2%" }}>
                    <Typography
                        variant="h5"
                        mb={1}
                        fontWeight="bold"
                        textAlign="left"
                        color="#0E4961"
                    >
                        Order Summary
                    </Typography>

                    <Card sx={{ flex: 1, display: "flex", flexDirection: "column", borderRadius: "20px" }}>
                        <CardContent sx={{ flex: 1 }}>
                            <Grid container justifyContent="space-between" p={1}>
                                <Grid item sx={{ textAlign: "left" }}>
                                    <Typography variant="subtitle1" fontWeight="medium">Item(s)</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">Total Sale Price</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">Total Tax Price</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">Platform Charges</Typography>
                                    {/* FIX 12: savedAmount now displayed */}
                                    <Typography variant="subtitle1" fontWeight="medium" color="green">You Save</Typography>
                                </Grid>
                                <Grid item sx={{ textAlign: "right" }}>
                                    <Typography variant="subtitle1" fontWeight="medium">{prices.quantity}</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">₹ {prices.totalTaxable}</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">₹ {prices.totalTaxAmount}</Typography>
                                    <Typography variant="subtitle1" fontWeight="medium">
                                        ₹ {((additionalCharges?.platformFees || 0) + (additionalCharges?.GSTPlatformFees || 0)).toFixed(2)}
                                    </Typography>
                                    <Typography variant="subtitle1" fontWeight="medium" color="green">₹ {prices.savedAmount}</Typography>
                                </Grid>
                            </Grid>

                            <Box sx={{ border: "1px dashed #d6d7db" }} />

                            <Grid container justifyContent="space-between" mt={1}>
                                <Grid item>
                                    <Typography variant="h5" fontWeight="medium">Grand Total</Typography>
                                </Grid>
                                <Grid item>
                                    <Typography variant="h5" fontWeight="medium">₹ {prices.grandTotal}</Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Typography variant="h6" mb={1} mt={1} textAlign="left" color="#0E4961">
                        Estimated Order Arrival Time
                    </Typography>
                    <Typography
                        variant="h5"
                        fontSize={30}
                        fontWeight="bold"
                        mb={1}
                        textAlign="left"
                        color="#0E4961"
                    >
                        {cartItems.length > 0 ? makingTime : "00:00"}
                    </Typography>

                    {paymentType === "CARD_SWIPE" ? (
                        <>
                            <Grid container sx={{ display: "flex", justifyContent: "center", marginTop: "17px" }}>
                                {paymentModes.map((mode) => (
                                    <Grid item md={3} key={mode.id}>
                                        <Button
                                            variant="contained"
                                            style={{
                                                borderRadius: "4px",
                                                backgroundColor: selectedMode === mode.code ? "#00b0ff" : "#CCCCCC",
                                                color: "#fff",
                                                width: "62px",
                                                height: "60px",
                                                padding: "3px",
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                textAlign: "center",
                                                textTransform: "capitalize",
                                            }}
                                            onClick={() => handleModeSelect(mode.code)}
                                        >
                                            <img
                                                src={mode.src}
                                                alt={mode.name}
                                                style={{ minWidth: "25px", minHeight: "25px", marginBottom: "2px" }}
                                            />
                                            {mode.name}
                                        </Button>
                                    </Grid>
                                ))}
                            </Grid>
                            <PrimaryButton
                                title={fetchTime || !paymentEnabled ? "Please Wait..." : "PROCEED TO PAY"}
                                onClick={handlePinePayment}
                                spinner={btnLoading || fetchTime}
                                disabled={btnLoading || fetchTime || !paymentEnabled || payButtonLoading}
                                disabledTitle="Select Payment Mode"
                                style={{
                                    borderRadius: "30px",
                                    fontSize: "18px",
                                    width: "100%",
                                    marginTop: "10px",
                                    marginBottom: "30px",
                                    background: fetchTime || paymentEnabled ? "#00b0ff" : "#CCCCCC",
                                }}
                            />
                        </>
                    ) : (
                        <PrimaryButton
                            title="PROCEED TO PAY"
                            onClick={createOrder}
                            disabledTitle="Please Wait..."
                            disabled={payButtonLoading || cartItems.length === 0}
                            style={{
                                borderRadius: "30px",
                                fontSize: "18px",
                                width: "100%",
                                marginTop: "10px",
                                marginBottom: "20px",
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Pine Labs Payment Modal */}
            <Modal open={isPaymentModalOpen} aria-labelledby="payment-modal">
                <Box
                    sx={{
                        width: "400px",
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "8px",
                        margin: "auto",
                        marginTop: "20vh",
                        boxShadow: 24,
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h6">Processing Payment</Typography>
                    <Typography sx={{ marginTop: "10px", fontSize: "14px" }}>
                        Transaction ID: {txnId || "Waiting..."}
                    </Typography>
                    <Typography sx={{ marginTop: "10px", fontSize: "14px" }}>
                        Status: {paymentStatus}
                    </Typography>
                    <CircularProgress size={24} sx={{ marginTop: "10px" }} />
                    <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
                        <Button variant="contained" color="error" onClick={handlePinePaymentCancel}>
                            Cancel Payment
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
};

export default OrderPayment;